/**
 * Payment Utility Functions
 * Helper functions for payment calculations and formatting
 */

/**
 * Calculate renewal date based on renewal period
 * @param {string} period - Renewal period: 'weekly', 'monthly', '3months', or 'custom'
 * @param {number|null} customMonths - Number of months for custom period (required if period is 'custom')
 * @param {Date} startDate - Start date for calculation (defaults to now)
 * @returns {Date} Calculated renewal date
 */
export function calculateRenewalDate(period, customMonths = null, startDate = null) {
  const start = startDate ? new Date(startDate) : new Date();
  const renewalDate = new Date(start);

  switch (period) {
    case 'weekly':
      renewalDate.setDate(renewalDate.getDate() + 7);
      break;
    case 'monthly':
      renewalDate.setMonth(renewalDate.getMonth() + 1);
      break;
    case '3months':
      renewalDate.setMonth(renewalDate.getMonth() + 3);
      break;
    case '6months':
      renewalDate.setMonth(renewalDate.getMonth() + 6);
      break;
    case 'yearly':
      renewalDate.setFullYear(renewalDate.getFullYear() + 1);
      break;
    case 'custom':
      if (!customMonths || customMonths < 1) {
        throw new Error('Custom months must be at least 1');
      }
      renewalDate.setMonth(renewalDate.getMonth() + customMonths);
      break;
    default:
      throw new Error(`Invalid renewal period: ${period}`);
  }

  return renewalDate;
}

/**
 * Format renewal period for display
 * @param {string} period - Renewal period: 'weekly', 'monthly', '3months', or 'custom'
 * @param {number|null} customMonths - Number of months for custom period
 * @returns {string} Formatted period string
 */
export function formatRenewalPeriod(period, customMonths = null) {
  switch (period) {
    case 'weekly':
      return 'Weekly';
    case 'monthly':
      return 'Monthly';
    case '3months':
      return 'Every 3 Months';
    case '6months':
      return 'Every 6 Months';
    case 'yearly':
      return 'Yearly';
    case 'custom':
      if (customMonths === 1) {
        return 'Every Month';
      }
      return `Every ${customMonths} Months`;
    default:
      return period || 'N/A';
  }
}

/**
 * Convert renewal period to Stripe billing interval format
 * @param {string} period - Renewal period: 'weekly', 'monthly', '3months', or 'custom'
 * @param {number|null} customMonths - Number of months for custom period
 * @returns {Object} Stripe billing interval object with interval and interval_count
 */
export function getRenewalIntervalStripe(period, customMonths = null) {
  switch (period) {
    case 'weekly':
      return {
        interval: 'week',
        interval_count: 1,
      };
    case 'monthly':
      return {
        interval: 'month',
        interval_count: 1,
      };
    case '3months':
      return {
        interval: 'month',
        interval_count: 3,
      };
    case '6months':
      return {
        interval: 'month',
        interval_count: 6,
      };
    case 'yearly':
      return {
        interval: 'year',
        interval_count: 1,
      };
    case 'custom':
      if (!customMonths || customMonths < 1) {
        throw new Error('Custom months must be at least 1');
      }
      return {
        interval: 'month',
        interval_count: customMonths,
      };
    default:
      throw new Error(`Invalid renewal period: ${period}`);
  }
}

/**
 * Format renewal date for display
 * @param {Date|string} renewalDate - Renewal date
 * @returns {string} Formatted date string
 */
export function formatRenewalDate(renewalDate) {
  if (!renewalDate) return 'N/A';
  
  const date = renewalDate instanceof Date ? renewalDate : new Date(renewalDate);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

/**
 * Check if renewal date is upcoming (within specified days)
 * @param {Date|string} renewalDate - Renewal date
 * @param {number} days - Number of days to check (default: 7)
 * @returns {boolean} True if renewal is within the specified days
 */
export function isRenewalUpcoming(renewalDate, days = 7) {
  if (!renewalDate) return false;
  
  const date = renewalDate instanceof Date ? renewalDate : new Date(renewalDate);
  const now = new Date();
  const diffTime = date.getTime() - now.getTime();
  const diffDays = diffTime / (1000 * 60 * 60 * 24);
  
  return diffDays >= 0 && diffDays <= days;
}

/**
 * Format service term for display
 * @param {string} period - Renewal period: 'weekly', 'monthly', '3months', '6months', 'yearly', or 'custom'
 * @param {number} intervalMonths - Number of months for custom periods
 * @returns {string} Formatted term string (e.g., "Monthly", "Every 3 Months", "Yearly")
 */
export function formatServiceTerm(period, intervalMonths = null) {
  switch (period) {
    case 'weekly':
      return 'Weekly';
    case 'monthly':
      return 'Monthly';
    case '3months':
      return 'Every 3 Months';
    case '6months':
      return 'Every 6 Months';
    case 'yearly':
      return 'Yearly';
    case 'custom':
      if (intervalMonths) {
        return `Every ${intervalMonths} ${intervalMonths === 1 ? 'Month' : 'Months'}`;
      }
      return 'Custom';
    default:
      return period || 'N/A';
  }
}

/**
 * Get price suffix for service term display
 * @param {string} period - Renewal period
 * @param {number} intervalMonths - Number of months for custom periods
 * @returns {string} Price suffix (e.g., "/mo", "/3mo", "/yr")
 */
export function getServicePriceSuffix(period, intervalMonths = null) {
  switch (period) {
    case 'weekly':
      return '/wk';
    case 'monthly':
      return '/mo';
    case '3months':
      return '/3mo';
    case '6months':
      return '/6mo';
    case 'yearly':
      return '/yr';
    case 'custom':
      if (intervalMonths) {
        return `/${intervalMonths}mo`;
      }
      return '';
    default:
      return '';
  }
}
